import { useMutation, useQueryClient } from '@tanstack/react-query'
import { toast } from 'react-toastify'
import { useTranslation } from 'react-i18next'
import instance from '../api'
import { useAuthContext } from '../../context/auth/auth'
import { CURRENT_SUBSCRIPTION_QUERY_KEY } from './useCurrentSubscription'

// Notification only - it never gates activation. The backend stamps
// `payment_notified_at` and emails billing; repeat calls within 24h are
// accepted without sending a second email.
const confirmPayment = async (subscriptionId: number, token: string | null) => {
  return instance.post(
    `/payment/subscriptions/${subscriptionId}/notify-payment/`,
    {},
    { headers: token ? { Authorization: `Bearer ${token}` } : undefined },
  )
}

const useConfirmPayment = () => {
  const { t } = useTranslation()
  const { token } = useAuthContext()
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ subscriptionId }: { subscriptionId: number }) =>
      confirmPayment(subscriptionId, token),
    onSuccess: async () => {
      // The call stamps `payment_notified_at`; refetch so the cached
      // subscription stops claiming the customer has never notified us.
      await queryClient.invalidateQueries({
        queryKey: CURRENT_SUBSCRIPTION_QUERY_KEY,
      })
      toast.success(t('paymentConfirmationSent'))
    },
    onError: () => {
      toast.error(t('failedToConfirmPayment'))
    },
  })
}

export default useConfirmPayment
